import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { StatusAtendimento } from "../../interfaces/atendimento.interface";

interface Patient {
  name: string;
  cpf: string;
  age?: number;
  dataNascimento?: string;
  insurance?: string;
}

interface Doctor {
  name: string;
  specialty: string;
}

interface HeaderProps {
  patient: Patient;
  doctor: Doctor;
  riskScore: string;
  onBack: () => void;
  status?: StatusAtendimento | string | null;
  atendimentoId?: number;
  onStatusChange?: (status: StatusAtendimento) => void;
}

export const Header = ({ patient, doctor, riskScore, onBack, status, atendimentoId, onStatusChange }: HeaderProps) => {
  // Função para calcular idade a partir da data de nascimento
  const calculateAge = (dataNascimento?: string): number => {
    if (!dataNascimento) return 0;
    try {
      const nascimento = new Date(dataNascimento);
      if (isNaN(nascimento.getTime())) return 0;
      const hoje = new Date();
      let age = hoje.getFullYear() - nascimento.getFullYear();
      const mes = hoje.getMonth() - nascimento.getMonth();
      if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
        age--;
      }
      return age;
    } catch {
      return 0;
    }
  };

  // Calcular idade se não estiver disponível mas tiver data de nascimento
  const patientAge = (patient?.age && patient.age > 0) 
    ? patient.age 
    : (patient?.dataNascimento ? calculateAge(patient.dataNascimento) : 0);

  const handleStatusChange = async (newStatus: string) => {
    if (!atendimentoId) {
      toast({
        title: "Erro",
        description: "ID do atendimento não fornecido.",
        variant: "destructive"
      });
      return;
    }

    const statusValue = newStatus as StatusAtendimento;
    
    try {
      // Atualizar via API
      const { api } = await import("@/lib/api");
      await api.put(`/api/atendimentos/${atendimentoId}`, {
        status_atendimento: statusValue
      });

      // Notificar o componente pai
      onStatusChange?.(statusValue);

      toast({
        title: "Status atualizado",
        description: `Status alterado para: ${getStatusLabel(statusValue)}`,
      });
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro",
        description: error?.message || "Não foi possível atualizar o status.",
        variant: "destructive"
      });
    }
  };

  const getStatusLabel = (status: StatusAtendimento | string | null | undefined): string => {
    if (!status) return "Em Andamento";
    
    switch (status) {
      case StatusAtendimento.EM_ANDAMENTO:
        return "Em Andamento";
      case StatusAtendimento.CONCLUIDO:
        return "Concluído";
      case StatusAtendimento.CANCELADO:
        return "Cancelado";
      case StatusAtendimento.AGENDADO:
        return "Agendado";
      case StatusAtendimento.REAGENDADO:
        return "Reagendado";
      default:
        return String(status);
    }
  };

  const getStatusColor = (status: StatusAtendimento | string | null | undefined): string => {
    switch (status) {
      case StatusAtendimento.CONCLUIDO:
        return "bg-success/10 text-success";
      case StatusAtendimento.CANCELADO:
        return "bg-red-100 text-red-800";
      case StatusAtendimento.EM_ANDAMENTO:
        return "bg-warning/10 text-warning";
      default:
        return "bg-primary/10 text-primary";
    }
  };

  const currentStatus = (status as StatusAtendimento) || StatusAtendimento.EM_ANDAMENTO;

  return (
    <Card className="sticky top-0 z-10 shadow-md">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          
          {/* <Badge className={riskScore === "Baixo" ? "bg-success/10 text-success" : ""}>
            Escore Lee: {riskScore}
          </Badge> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Paciente</p>
            <p className="font-semibold text-lg">{patient?.name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Idade / Convênio</p>
            <p className="font-medium">{patientAge > 0 ? `${patientAge} anos` : "N/A"}</p>
            <p className="text-sm">{patient?.insurance || "Particular"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Médico Responsável</p>
            <p className="font-medium">{doctor.name}</p>
            <p className="text-sm text-muted-foreground">{doctor.specialty}</p>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Status</Label>
            <Select
              value={currentStatus}
              onValueChange={handleStatusChange}
              disabled={!atendimentoId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione o status">
                  <Badge variant="secondary" className={getStatusColor(currentStatus)}>
                    {getStatusLabel(currentStatus)}
                  </Badge>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={StatusAtendimento.EM_ANDAMENTO}>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={getStatusColor(StatusAtendimento.EM_ANDAMENTO)}>
                      Em Andamento
                    </Badge>
                  </div>
                </SelectItem>
                <SelectItem value={StatusAtendimento.CONCLUIDO}>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={getStatusColor(StatusAtendimento.CONCLUIDO)}>
                      Concluído
                    </Badge>
                  </div>
                </SelectItem>
                <SelectItem value={StatusAtendimento.CANCELADO}>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={getStatusColor(StatusAtendimento.CANCELADO)}>
                      Cancelado
                    </Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </Card>
  );
};

